/* eslint-disable max-lines-per-function */
import { toast } from '@/hooks/use-toast';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Body,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@e2n/ui/v2';
import { Abschnitt } from '@/domain/mitarbeiter/features/rotationsplan/entities/Abschnitt.ts';
import { Rotationsplan } from '@/domain/mitarbeiter/features/rotationsplan/entities/Rotationsplan.ts';
import { useDeleteAbschnitt } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useDeleteAbschnitt/useDeleteAbschnitt.ts';

export function DeleteAbschnitt({
  abschnitt,
  rotationsplan,
}: {
  abschnitt: Abschnitt;
  rotationsplan: Rotationsplan;
}) {
  const { id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [deleteAbschnitt] = useDeleteAbschnitt();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const istLehrjahr = rotationsplan.beruf.istAusbildung;

  const keineStationenVorhanden = abschnitt.stationen.length === 0;

  async function handleDelete() {
    setIsSubmitting(true);
    const result = await deleteAbschnitt({
      abschnittId: abschnitt.id,
      rotationsplanId: rotationsplan.id,
      position: abschnitt.position,
    });

    if (result.error || result.data?.mitarbeiter_deleteAbschnitt.error) {
      toast({
        description: t(
          istLehrjahr
            ? 'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.error.lehrjahr'
            : 'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.error.abschnitt',
        ),
      });
    } else {
      navigate(`/mitarbeiter/rotationsplan/${id}`);
      toast({
        description: t(
          istLehrjahr
            ? 'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.success.lehrjahr'
            : 'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.success.abschnitt',
        ),
      });
    }
    setIsSubmitting(false);
  }

  return (
    <>
      {/*Desktop Dialog*/}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="hidden md:flex">
          <DialogHeader>
            <DialogTitle>
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.title',
                { name: abschnitt.position + '. ' + abschnitt.name },
              )}
            </DialogTitle>
            <div className="flex flex-col gap-2">
              <DialogDescription>
                {t(
                  istLehrjahr
                    ? 'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.subtitle.lehrjahr'
                    : 'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.subtitle.abschnitt',
                )}
              </DialogDescription>
              <Body className="text-error-red-200">
                {t(
                  'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.infoStandortwechsel',
                )}
              </Body>
            </div>
          </DialogHeader>
          <DialogFooter variant="default">
            <Button
              onClick={() => {
                setIsDialogOpen(false);
              }}
              variant="secondary-blue">
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.dismiss',
              )}
            </Button>
            <Button
              disabled={isSubmitting}
              variant="primary-pill"
              onClick={handleDelete}>
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.confirm',
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild className="hidden md:flex">
            <Button
              disabled={isSubmitting}
              onClick={() => {
                if (keineStationenVorhanden) {
                  handleDelete();
                } else {
                  setIsDialogOpen(true);
                }
              }}
              variant="secondary-red"
              iconOnly>
              <FontAwesomeIcon icon={faTrashCan} className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side={'bottom'}>
            {t(
              'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.tooltip',
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/*Mobile Drawer*/}
      <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <DrawerTrigger className="flex md:hidden" asChild>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild className="flex md:hidden">
                <Button
                  disabled={isSubmitting}
                  onClick={() => {
                    if (keineStationenVorhanden) {
                      handleDelete();
                    } else {
                      setIsDrawerOpen(true);
                    }
                  }}
                  variant="secondary-red"
                  iconOnly>
                  <FontAwesomeIcon icon={faTrashCan} className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side={'bottom'}>
                {t(
                  'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.tooltip',
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </DrawerTrigger>
        <DrawerContent className="md:hidden">
          <DrawerHeader>
            <DrawerTitle>
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.title',
                { name: abschnitt.position + '. ' + abschnitt.name },
              )}
            </DrawerTitle>
          </DrawerHeader>
          <DrawerBody>
            <div className="flex flex-col gap-2">
              <DrawerDescription>
                {t(
                  istLehrjahr
                    ? 'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.subtitle.lehrjahr'
                    : 'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.subtitle.abschnitt',
                )}
              </DrawerDescription>
              <Body className="text-center text-error-red-200">
                {t(
                  'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.infoStandortwechsel',
                )}
              </Body>
            </div>
            <DrawerFooter>
              <Button
                variant="primary-pill"
                disabled={isSubmitting}
                onClick={handleDelete}>
                {t(
                  'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.confirm',
                )}
              </Button>
              <Button
                onClick={() => {
                  setIsDrawerOpen(false);
                }}
                variant="secondary-blue">
                {t(
                  'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.dismiss',
                )}
              </Button>
            </DrawerFooter>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}
